import { useState, useMemo, useEffect } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useOrders, useBatchUpdateOrders, useDeleteOrder, useShippingMethods, usePaymentGateways } from "@/hooks/use-orders";
import { useGeneratePickupLabels } from "@/hooks/use-fulfillment";
import { useActiveStore } from "@/hooks/use-active-store";
import { useSession } from "@/hooks/use-auth";
import { hasRetail, hasRole } from "@/lib/roles";
import { OrderFilters } from "@/components/orders/order-filters";
import { StatusDot, statusDotClass, ORDER_STATUSES } from "@/components/orders/status-badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { RotateCcw, Trash2, ShieldBan, Plus, Truck, Repeat, Printer, StickyNote, Package, PackageCheck, MessageSquare, AlertTriangle, PackageX } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useCheckBannedBulk } from "@/hooks/use-banned-customers";
import { useCheckCustomerReturnsBulk } from "@/hooks/use-package-returns";
import { RETURN_REASON_LABEL } from "@/lib/package-return-labels";
import { toast } from "sonner";
import { cn, formatCurrency, formatDate } from "@/lib/utils";
import { BulkImportDialog } from "@/components/shipping/bulk-import-dialog";
import { ResetShippingDialog } from "@/components/shipping/reset-shipping-dialog";
import { TablePagination } from "@/components/ui/table-pagination";
import { useShipmentsBulk } from "@/hooks/use-shipping";
import { useMetaBadges } from "@/hooks/use-meta-orders";
import { MetaListBadge } from "@/components/meta-orders/meta-badge";
import { useStoreSyncStatus } from "@/hooks/use-stores";
import { detectOrderSearchScope, type OrderSearchScope } from "@/lib/order-search-scope";

// Rows carry a lot of small signals (notes, bans, exchanges). One icon + tooltip
// component keeps the row markup readable instead of repeating the Tooltip stack.
function RowFlag({
  icon: Icon,
  className,
  label,
  detail,
}: {
  icon: LucideIcon;
  className?: string;
  label: string;
  detail?: string;
}) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Icon className={cn("h-3.5 w-3.5 shrink-0", className)} />
        </TooltipTrigger>
        <TooltipContent className="max-w-xs whitespace-pre-wrap">
          <span className={cn("font-medium", detail && "block mb-0.5")}>{label}</span>
          {detail}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// A short status-coloured tick in the left margin of each row. Kept row-height
// rather than full-bleed so a page of one status reads as ticks, not a solid rail.
function StatusTick({ status }: { status: string }) {
  return (
    <span
      aria-hidden
      className={cn(
        "absolute left-0 top-1/2 h-4 w-[2px] -translate-y-1/2",
        statusDotClass(status),
      )}
    />
  );
}

// Today's orders are the ones being worked on, so they read as a bare time;
// anything older keeps its date. Time is what the packing table sorts by.
function formatDayTime(date: string | null | undefined) {
  if (!date) return "—";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "—";
  const time = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  const today = new Date();
  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (sameDay(d, today)) return time;
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (sameDay(d, yesterday)) return `jučer ${time}`;
  return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}. ${time}`;
}

// "Nije poslano" is only news while an order is still waiting to go out. On a
// completed or cancelled order a missing shipment record says nothing useful.
const AWAITING_SHIPMENT = new Set(["pending", "processing", "on-hold"]);

function plural(n: number, one: string, few: string, many: string) {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
}

function itemsLabel(lines: number, quantity: number) {
  const base = `${lines} ${plural(lines, "stavka", "stavke", "stavki")}`;
  return quantity > lines ? `${base} · ${quantity} kom` : base;
}

function returnedPackagesLabel(count: number) {
  return plural(count, "vraćen paket", "vraćena paketa", "vraćenih paketa");
}

export function OrdersPage() {
  const { storeId } = useActiveStore();
  const { user } = useSession();
  const navigate = useNavigate();
  const canEdit = hasRole(user, "admin") || hasRole(user, "manager");

  // Nalog bez maloprodajne role nema pristup maloprodajnim narudžbama — preusmjeri ga na
  // /wholesale/orders. Efekat (a ne rani return prije hookova) jer se sesija
  // razrješava asinhrono na istom mountu (bez remounta): rani return bi ovdje
  // prekršio Rules of Hooks čim se user popuni.
  useEffect(() => {
    if (user && !hasRetail(user)) {
      navigate({ to: "/wholesale/orders", replace: true });
    }
  }, [user, navigate]);

  // Arriving from the ⌘K palette with ?search= — start on "Sve" so the match isn't
  // hidden by the default "U obradi" filter.
  const { search: initialSearch } = useSearch({ from: "/_app/orders" });

  // Webhook (mirror) stores can OR customer and item matches in a single query; API
  // stores still hit the WP plugin, which only knows the two separate params.
  const { data: syncStatus } = useStoreSyncStatus(storeId);
  const unifiedSearch = syncStatus?.syncMode === "webhook";

  const [status, setStatus] = useState(initialSearch ? "" : "processing");
  const [search, setSearch] = useState(initialSearch ?? "");
  const [itemSearch, setItemSearch] = useState("");
  const [q, setQ] = useState(initialSearch ?? "");
  const [qScope, setQScope] = useState<OrderSearchScope | null>(null);
  const [paymentMethod, setPaymentMethod] = useState("");
  const [shippingMethod, setShippingMethod] = useState("");
  const [dateAfter, setDateAfter] = useState("");
  const [dateBefore, setDateBefore] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulkStatus, setBulkStatus] = useState("");
  const [shippedFilter, setShippedFilter] = useState("all");
  const [importOpen, setImportOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: number; number: string } | null>(null);

  const filters: Record<string, string> = { page: String(page), per_page: "20" };
  if (status) filters.status = status;
  if (unifiedSearch) {
    if (q) {
      filters.q = q;
      filters.q_scope = qScope ?? detectOrderSearchScope(q);
    }
  } else {
    if (search) filters.search = search;
    if (itemSearch) filters.item_search = itemSearch;
  }
  if (paymentMethod && paymentMethod !== "all") filters.payment_method = paymentMethod;
  if (shippingMethod && shippingMethod !== "all") filters.shipping_method = shippingMethod;
  if (dateAfter) filters.after = new Date(dateAfter).toISOString();
  if (dateBefore) filters.before = new Date(dateBefore).toISOString();

  const { data, isLoading, error } = useOrders(storeId, filters);
  const batchUpdate = useBatchUpdateOrders(storeId);
  const deleteOrder = useDeleteOrder(storeId);
  const { data: shippingMethods } = useShippingMethods(storeId);
  const { data: paymentGateways } = usePaymentGateways(storeId);
  const generatePickupLabels = useGeneratePickupLabels();

  async function handlePrintPickupLabels() {
    if (!storeId || selected.size === 0) return;
    try {
      await generatePickupLabels.mutateAsync({
        orderIds: Array.from(selected).map(String),
        storeId,
      });
    } catch (e: any) {
      toast.error(e?.message || "Greška pri generisanju naljepnica");
    }
  }

  const bannedCustomers = useMemo(() => {
    if (!data?.orders) return [];
    return data.orders.map((o: any) => ({
      orderId: String(o.id),
      email: o.customer_email,
      phone: o.customer_phone,
    }));
  }, [data?.orders]);
  const { data: bannedMap } = useCheckBannedBulk(bannedCustomers);
  const { data: customerReturnsMap } = useCheckCustomerReturnsBulk(bannedCustomers);

  const orderIdsOnPage = useMemo(
    () => (data?.orders || []).map((o: any) => String(o.id)),
    [data?.orders],
  );
  const { data: shipmentsMap } = useShipmentsBulk(storeId, orderIdsOnPage);
  // Oznake su cross-shop (kandidati i meta narudžbe preko svih shopova), lista ostaje po shopu.
  const { data: metaBadges } = useMetaBadges(storeId, orderIdsOnPage);

  const visibleOrders = useMemo(() => {
    const orders: any[] = data?.orders || [];
    if (shippedFilter === "all" || !shipmentsMap) return orders;
    return orders.filter((o) => {
      const ship = shipmentsMap[String(o.id)];
      const isShipped = !!(ship && ship.status === "created" && ship.trackingNumber);
      return shippedFilter === "shipped" ? isShipped : !isShipped;
    });
  }, [data?.orders, shipmentsMap, shippedFilter]);

  // Sav render nakon ovog reda je maloprodajna stranica — dok se preusmjerenje
  // iz efekta gore ne izvrši, ne prikazuj je nalogu bez maloprodaje.
  if (user && !hasRetail(user)) return null;

  if (!storeId) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground">
          {hasRole(user, "admin") ? (
            <>Dodajte trgovinu u <Link to="/stores" className="underline">postavkama trgovina</Link></>
          ) : (
            "Kontaktirajte administratora za dodavanje trgovine"
          )}
        </p>
      </div>
    );
  }

  const isLocalPickup = (o: any) => o?.shipping_method_id === "local_pickup";
  const selectableOrders = visibleOrders.filter((o: any) => !isLocalPickup(o));

  function toggleSelect(order: any) {
    if (isLocalPickup(order)) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(order.id)) next.delete(order.id); else next.add(order.id);
      return next;
    });
  }

  function toggleAll() {
    if (!selectableOrders.length) return;
    if (selected.size === selectableOrders.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(selectableOrders.map((o: any) => o.id)));
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      await deleteOrder.mutateAsync(deleteTarget.id);
      toast.success(`Narudžba #${deleteTarget.number} obrisana`);
      setDeleteTarget(null);
      setSelected((prev) => { const next = new Set(prev); next.delete(deleteTarget.id); return next; });
    } catch (e: any) {
      toast.error(e?.message || "Greška pri brisanju narudžbe");
    }
  }

  async function handleBulkUpdate() {
    if (!bulkStatus || selected.size === 0) return;
    try {
      await batchUpdate.mutateAsync({
        update: Array.from(selected).map((id) => ({ id, status: bulkStatus })),
      });
      toast.success(`${selected.size} narudžbi ažurirano`);
      setSelected(new Set());
      setBulkStatus("");
    } catch (e: any) {
      toast.error(e?.message || "Greška pri ažuriranju narudžbi");
    }
  }

  // Dok preusmjerenje na veleprodaju ne prođe, ne bljeskaj maloprodajnu listu.
  if (user && !hasRetail(user)) {
    return (
      <div className="p-4 flex flex-col gap-2">
        {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
      </div>
    );
  }

  return (
    <div className="p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium">Narudžbe</h2>
        {canEdit && (
          <Link to="/orders/new">
            <Button size="sm" variant="outline">
              <Plus className="h-3.5 w-3.5 mr-1" /> Nova narudžba
            </Button>
          </Link>
        )}
      </div>

      <OrderFilters
        status={status}
        search={search}
        itemSearch={itemSearch}
        unifiedSearch={unifiedSearch}
        q={q}
        qScope={qScope}
        onQChange={(v) => { setQ(v); setPage(1); }}
        onQScopeChange={(v) => { setQScope(v); setPage(1); }}
        paymentMethod={paymentMethod}
        shippingMethod={shippingMethod}
        dateAfter={dateAfter}
        dateBefore={dateBefore}
        onStatusChange={(s) => { setStatus(s); setPage(1); }}
        onSearchChange={(s) => { setSearch(s); setPage(1); }}
        onItemSearchChange={(s) => { setItemSearch(s); setPage(1); }}
        onPaymentMethodChange={(m) => { setPaymentMethod(m); setPage(1); }}
        onShippingMethodChange={(m) => { setShippingMethod(m); setPage(1); }}
        onDateAfterChange={setDateAfter}
        onDateBeforeChange={setDateBefore}
        shippedFilter={shippedFilter}
        onShippedFilterChange={(v) => { setShippedFilter(v === "all" ? "all" : v); setPage(1); }}
        shippingMethods={shippingMethods}
        paymentGateways={paymentGateways}
      />

      {canEdit && selected.size > 0 && (
        <div className="flex items-center gap-2 bg-muted p-2 ring-1 ring-foreground/10">
          <span className="text-xs">{selected.size} odabrano</span>
          <Select value={bulkStatus} onValueChange={setBulkStatus}>
            <SelectTrigger className="w-40 h-8">
              <SelectValue placeholder="Novi status" />
            </SelectTrigger>
            <SelectContent>
              {ORDER_STATUSES.filter((s) => s.value).map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" onClick={handleBulkUpdate} disabled={!bulkStatus || batchUpdate.isPending}>
            Primijeni
          </Button>
          <div className="w-px h-5 bg-foreground/10" />
          <Button size="sm" variant="outline" onClick={() => setImportOpen(true)} title="Pošalji kuriru">
            <Truck className="h-4 w-4 mr-1" /> Pošalji kuriru
          </Button>
          <Button size="sm" variant="outline" onClick={() => setResetOpen(true)} title="Resetuj dostavu">
            <RotateCcw className="h-4 w-4 mr-1" /> Resetuj
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={handlePrintPickupLabels}
            disabled={generatePickupLabels.isPending}
            title="Naljepnica za lokalno preuzimanje"
          >
            <Printer className="h-4 w-4 mr-1" /> Preuzimanje
          </Button>
        </div>
      )}

      {error && <p className="text-destructive text-xs">{(error as Error).message}</p>}

      {isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
        </div>
      ) : (
        <div className="ring-1 ring-foreground/10 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b bg-muted/50 text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
                {canEdit && (
                  <th className="w-9 py-2 pl-3 pr-1 text-left">
                    <Checkbox
                      checked={selectableOrders.length > 0 && selected.size === selectableOrders.length}
                      disabled={selectableOrders.length === 0}
                      onCheckedChange={toggleAll}
                    />
                  </th>
                )}
                <th className={cn("py-2 pr-3 text-left font-medium", !canEdit && "pl-3")}>Narudžba</th>
                <th className="py-2 pr-3 text-left font-medium">Kupac</th>
                <th className="py-2 pr-3 text-left font-medium">Status</th>
                <th className="py-2 pr-3 text-left font-medium hidden md:table-cell">Dostava</th>
                <th className="py-2 pr-3 text-right font-medium">Ukupno</th>
                {canEdit && status === "cancelled" && <th className="w-10"></th>}
              </tr>
            </thead>
            <tbody>
              {visibleOrders.map((order: any) => {
                const ship = shipmentsMap?.[String(order.id)];
                const isShipped = !!(ship && ship.status === "created" && ship.trackingNumber);
                const ban = bannedMap?.[String(order.id)];
                const metaBadge = metaBadges?.[String(order.id)];
                const ret = customerReturnsMap?.[String(order.id)];
                const localPickup = isLocalPickup(order);
                const qty = Array.isArray(order.line_items)
                  ? order.line_items.reduce((n: number, it: any) => n + (Number(it?.quantity) || 0), 0)
                  : 0;
                return (
                <tr key={order.id} className="border-b last:border-b-0 hover:bg-muted/40 transition-colors">
                  {canEdit && (
                    <td className="relative py-2.5 pl-3 pr-1 align-middle">
                      <StatusTick status={order.status} />
                      {localPickup ? (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex">
                                <Checkbox checked={false} disabled />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>Lokalno preuzimanje — ne šalje se kuriru</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        <Checkbox
                          checked={selected.has(order.id)}
                          onCheckedChange={() => toggleSelect(order)}
                        />
                      )}
                    </td>
                  )}
                  <td className={cn("py-2.5 pr-3 align-middle", !canEdit && "relative pl-3")}>
                    {!canEdit && <StatusTick status={order.status} />}
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Link
                        to="/orders/$orderId"
                        params={{ orderId: String(order.id) }}
                        className="font-medium tabular-nums hover:underline underline-offset-2"
                      >
                        #{order.number}
                      </Link>
                      {order.is_exchange && (
                        <RowFlag icon={Repeat} className="text-amber-600" label="Zamjenska pošiljka" />
                      )}
                      {order.admin_note && String(order.admin_note).trim() && (
                        <RowFlag
                          icon={AlertTriangle}
                          className="text-red-600"
                          label="Admin napomena"
                          detail={String(order.admin_note)}
                        />
                      )}
                      {order.internal_note && String(order.internal_note).trim() && (
                        <RowFlag
                          icon={StickyNote}
                          className="text-amber-500"
                          label="Interna napomena"
                          detail={String(order.internal_note)}
                        />
                      )}
                      {order.customer_note && String(order.customer_note).trim() && (
                        <RowFlag
                          icon={MessageSquare}
                          className="text-blue-500"
                          label="Napomena kupca"
                          detail={String(order.customer_note)}
                        />
                      )}
                      {metaBadge && <MetaListBadge badge={metaBadge} />}
                    </div>
                    <div className="mt-0.5 text-[10px] text-muted-foreground tabular-nums">
                      {formatDayTime(order.date_created)}
                    </div>
                  </td>
                  <td className="py-2.5 pr-3 align-middle">
                    <div className="flex items-center gap-1.5">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="truncate max-w-[220px] inline-block align-middle">
                              {order.customer_name}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>{order.customer_name}</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      {ban && (
                        <RowFlag
                          icon={ShieldBan}
                          className="text-destructive"
                          label={`Banovan${ban.reason ? `: ${ban.reason}` : ""}`}
                        />
                      )}
                      {ret && (
                        <RowFlag
                          icon={PackageX}
                          className="text-orange-500"
                          label={`Kupac ima ${ret.count} ${returnedPackagesLabel(ret.count)}${
                            ret.reasons.length > 0
                              ? `: ${ret.reasons.map((r) => RETURN_REASON_LABEL[r] ?? r).join(", ")}`
                              : ""
                          }`}
                        />
                      )}
                    </div>
                    <div className="mt-0.5 text-[10px] text-muted-foreground truncate max-w-[240px]">
                      {[order.customer_city, order.customer_phone].filter(Boolean).join(" · ") || "—"}
                    </div>
                  </td>
                  <td className="py-2.5 pr-3 align-middle">
                    <StatusDot status={order.status} />
                    <div className="mt-0.5 text-[10px]">
                      {localPickup ? (
                        <span className="inline-flex items-center gap-1 text-muted-foreground">
                          <PackageCheck className="h-3 w-3 shrink-0" /> Lokalno preuzimanje
                        </span>
                      ) : isShipped ? (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex items-center gap-1 text-emerald-600 tabular-nums">
                                <PackageCheck className="h-3 w-3 shrink-0" />
                                {ship.trackingNumber}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <div className="text-xs">
                                <div className="font-medium">Uvezeno kuriru</div>
                                {ship.credentialName && <div>Kurir: {ship.credentialName}</div>}
                                <div className="text-muted-foreground">
                                  {formatDate(new Date(ship.createdAt * 1000).toISOString())}
                                </div>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : AWAITING_SHIPMENT.has(order.status) ? (
                        <span className="inline-flex items-center gap-1 text-muted-foreground">
                          <Package className="h-3 w-3 shrink-0" /> Nije poslano
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </div>
                  </td>
                  <td className="py-2.5 pr-3 align-middle hidden md:table-cell">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="truncate max-w-[240px] inline-block align-middle">
                            {order.shipping_method || "—"}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>{order.shipping_method || "Bez dostave"}</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <div className="mt-0.5 text-[10px] text-muted-foreground truncate max-w-[240px]">
                      {order.payment_method || "—"}
                    </div>
                  </td>
                  <td className="py-2.5 pr-3 text-right align-middle">
                    <div className="text-[13px] font-medium tabular-nums whitespace-nowrap">
                      {formatCurrency(parseFloat(order.total || "0"))}
                      <span className="ml-1 text-[10px] font-normal text-muted-foreground">{order.currency}</span>
                    </div>
                    <div className="mt-0.5 text-[10px] text-muted-foreground tabular-nums whitespace-nowrap">
                      {itemsLabel(order.item_count ?? 0, qty)}
                    </div>
                  </td>
                  {canEdit && status === "cancelled" && (
                    <td className="py-2.5 pr-2 align-middle">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteTarget({ id: order.id, number: order.number })}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  )}
                </tr>
                );
              })}
              {data?.orders?.length === 0 && (
                <tr>
                  <td
                    colSpan={canEdit ? (status === "cancelled" ? 7 : 6) : 5}
                    className="p-8 text-center text-muted-foreground"
                  >
                    Nema narudžbi za ove filtere
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {shippedFilter !== "all" && data?.orders && visibleOrders.length !== data.orders.length && (
        <p className="text-[11px] text-muted-foreground">
          Filter "{shippedFilter === "shipped" ? "Samo uvezene" : "Samo neuvezene"}" se primjenjuje na trenutnu stranicu ({visibleOrders.length} od {data.orders.length}).
        </p>
      )}
      {data && <TablePagination page={page} totalPages={data.totalPages} onPageChange={setPage} />}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Obriši narudžbu</DialogTitle>
            <DialogDescription>
              Jeste li sigurni da želite obrisati narudžbu #{deleteTarget?.number}? Narudžba će biti premještena u smeće.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Otkaži</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleteOrder.isPending}>Obriši</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {storeId && (
        <>
          <BulkImportDialog
            open={importOpen}
            onOpenChange={setImportOpen}
            selectedOrderIds={Array.from(selected).map(String)}
            storeId={storeId}
            onSuccess={() => setSelected(new Set())}
          />
          <ResetShippingDialog
            open={resetOpen}
            onOpenChange={setResetOpen}
            selectedOrderIds={Array.from(selected).map(String)}
            storeId={storeId}
            onSuccess={() => setSelected(new Set())}
          />
        </>
      )}
    </div>
  );
}
